﻿// ─────────────────────────────────────────────────────────────────────────────
// Сжатие геометрии маршрута (Google Encoded Polyline).
//
// Кодек без зависимостей от карты и бэкенда. Нужен, чтобы отправлять геометрию
// маршрута на бэкенд одной компактной строкой, а не JSON-массивом.
// ─────────────────────────────────────────────────────────────────────────────

export class RoutePolyline {
    // Разделитель путей внутри одной сжатой строки. Символ ';' (код 59) не
    // встречается в закодированных путях — они состоят только из ASCII 63..126.
    private static readonly separator = ';';

    // Кодирует одно целое число со знаком в строку.
    // Zigzag убирает знак (0→0, -1→1, 1→2, -2→3, 2→4 …), затем число пакуется
    // кусочками по 5 бит в печатные ASCII-символы (+63, бит продолжения +32).
    private static encodeSignedValue(value: number): string {
        const zigzag = value >= 0 ? value * 2 : -value * 2 - 1;

        let n = zigzag;
        let out = '';

        while (n >= 32) {
            out += String.fromCharCode(95 + (n % 32));
            n = Math.floor(n / 32);
        }

        out += String.fromCharCode(63 + n);
        return out;
    }

    // Читает одно закодированное число, начиная с позиции index.
    private static decodeSignedValue(encoded: string, index: number): { value: number; nextIndex: number } {
        let result = 0;
        let multiplier = 1;
        let byte: number;

        do {
            byte = encoded.charCodeAt(index) - 63;
            index += 1;

            result += (byte % 32) * multiplier;
            multiplier *= 32;
        }
        while (byte >= 32);

        const value = result % 2 === 0 ? result / 2 : -(result + 1) / 2;

        return { value, nextIndex: index };
    }

    // Сжимает один путь (массив [широта, долгота]): координаты округляем до 5 знаков
    // (~1 метр), храним дельту к предыдущей точке и пакуем в 1–2 символа.
    private static encodePath(points: number[][]): string {
        let prevLat = 0;
        let prevLng = 0;
        let encoded = '';

        for (const [lat, lng] of points) {
            const latInt = Math.round(lat * 100000);
            const lngInt = Math.round(lng * 100000);

            encoded += RoutePolyline.encodeSignedValue(latInt - prevLat);
            encoded += RoutePolyline.encodeSignedValue(lngInt - prevLng);

            prevLat = latInt;
            prevLng = lngInt;
        }

        return encoded;
    }

    // Раскодирует путь обратно в координаты.
    private static decodePath(encoded: string): number[][] {
        const points: number[][] = [];
        let lat = 0;
        let lng = 0;
        let index = 0;

        while (index < encoded.length) {
            const latDelta = RoutePolyline.decodeSignedValue(encoded, index);
            index = latDelta.nextIndex;
            lat += latDelta.value;

            const lngDelta = RoutePolyline.decodeSignedValue(encoded, index);
            index = lngDelta.nextIndex;
            lng += lngDelta.value;

            points.push([lat / 100000, lng / 100000]);
        }

        return points;
    }

    // Кодирует все пути маршрута и склеивает их в одну строку через ';'.
    static encodePaths(paths: number[][][]): string {
        return paths.map((path) => RoutePolyline.encodePath(path)).join(RoutePolyline.separator);
    }

    // Разбивает склеенную строку обратно на пути и декодирует каждый.
    static decodePaths(encoded: string): number[][][] {
        if (!encoded) {
            return [];
        }

        return encoded.split(RoutePolyline.separator).map((path) => RoutePolyline.decodePath(path));
    }
}
